import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface GrowthQuality {
  recurringRevenue: number;
  oneTimeRevenue: number;
  recurringShare: number;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Story 57.4 — growth quality. Recurring vs one-time revenue split
 * across all WON opportunities. The recurring share is the v2 doc's
 * "is the growth high quality?" signal.
 */
export function GrowthQualityWidget() {
  const [data, setData] = useState<GrowthQuality | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<GrowthQuality>('/analytics/growth-quality')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading growth quality…
      </div>
    );
  }
  if (!data) return null;

  const recurring = Number(data.recurringRevenue);
  const oneTime = Number(data.oneTimeRevenue);
  const total = recurring + oneTime;
  const share = Number(data.recurringShare);
  const recurringWidth = total > 0 ? (recurring / total) * 100 : 0;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Growth quality
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-muted-foreground">Recurring</div>
          <div className="mt-1 text-lg font-bold text-emerald-300">{fmt(recurring)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">One-time</div>
          <div className="mt-1 text-lg font-bold text-foreground">{fmt(oneTime)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Recurring share</div>
          <div className="mt-1 text-lg font-bold">
            {total > 0 ? `${(share * 100).toFixed(0)}%` : '—'}
          </div>
        </div>
      </div>
      {total > 0 && (
        <div className="mt-3 flex h-2 w-full overflow-hidden rounded bg-muted">
          <div
            className="h-2 bg-emerald-500/80"
            style={{ width: `${recurringWidth}%` }}
          />
          <div
            className="h-2 bg-zinc-400"
            style={{ width: `${100 - recurringWidth}%` }}
          />
        </div>
      )}
    </div>
  );
}
